import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiGrid, FiHash, FiMessageSquare, FiTrendingUp, FiRefreshCw,
  FiStar, FiBarChart2, FiFileText, FiZap, FiSettings, FiPaperclip,
} from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import aiLogo from '../../assets/images/M_Multicolor.svg';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import { useChannels } from '../../context/ChannelContext';
import { useAmpsLoaded } from '../../context/AmpContext';
import { useRunDetailPanel } from '../../context/RunDetailPanelContext';
import * as chatService from '../../services/chatService';
import { sendMarketingMessage } from '../../services/chatService';
import { dismissWelcomeChannel } from '../../services/channelService';
import * as historyService from '../../services/historyService';
import ArchiveChatButton from '../../components/ui/ArchiveChatButton';
import { buildAmpPrompts } from './ampPrompts';
import { useStatusTemplate } from '../../context/StatusTemplateContext';
import { useAutoScroll } from '../../hooks/useAutoScroll';
import { useTypewriter } from '../../hooks/useTypewriter';
import type { ChatMessage as ChatMessageType, ChatThread, FileAttachment, TableData, Amp, PlanStep } from './chat.types';
import WelcomeChannelPage from './WelcomeChannelPage';
import FilePreviewModal from '../../components/ui/FilePreviewModal';
import '../../styles/ChannelChat.css';
import '../../styles/ChatContainer.css';

function parseMarkdownTable(content: string): TableData | null {
  const lines = content.split('\n');
  const tableLines: string[] = [];
  let capturing = false;
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('|')) { capturing = true; tableLines.push(trimmed); }
    else if (capturing) break;
  }
  if (tableLines.length < 2) return null;
  const dataLines = tableLines.filter(l => !/^\|[\s:|-]+\|$/.test(l));
  if (dataLines.length < 2) return null;
  const parseRow = (line: string) => line.split('|').slice(1, -1).map(c => c.trim());
  return { headers: parseRow(dataLines[0]), rows: dataLines.slice(1).map(parseRow) };
}

function extractFilesFromMessageContent(content: string): FileAttachment[] {
  const rows: FileAttachment[] = [];
  const lines = (content || '').split('\n');
  let inAttachedSection = false;

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      if (inAttachedSection) break;
      continue;
    }
    if (/^\[?Attached files\]?\s*:?$/i.test(line)) {
      inAttachedSection = true;
      continue;
    }
    if (!inAttachedSection) continue;

    const bullet = line.replace(/^[-*]\s*/, '').trim();
    const withUrl = bullet.match(/^(.+?)\s*:\s*(https?:\/\/\S.*)$/i);
    if (withUrl) {
      rows.push({ name: withUrl[1].trim(), fileUrl: withUrl[2].trim(), size: 0, type: '' });
      continue;
    }

    const urlOnly = bullet.match(/^(https?:\/\/\S.*)$/i);
    if (!urlOnly) continue;

    const url = urlOnly[1].trim();
    const rawName = url.split('?')[0].split('/').pop() || 'Attached file';
    rows.push({ name: decodeURIComponent(rawName), fileUrl: url, size: 0, type: '' });
  }

  return rows;
}


const ICON_MAP: Record<string, React.ReactNode> = {
  hash: <FiHash size={20} />,
  message: <FiMessageSquare size={20} />,
  trending: <FiTrendingUp size={20} />,
  refresh: <FiRefreshCw size={20} />,
  star: <FiStar size={20} />,
  chart: <FiBarChart2 size={20} />,
};

const AMP_ICON_MAP: Record<string, React.ReactNode> = {
  message: <FiMessageSquare size={16} />,
  search: <FiMessageSquare size={16} />,
  phone: <FiMessageSquare size={16} />,
  file: <FiFileText size={16} />,
  image: <RiSparklingFill size={16} />,
  scan: <FiMessageSquare size={16} />,
  dollar: <FiBarChart2 size={16} />,
  chart: <FiBarChart2 size={16} />,
  shield: <FiMessageSquare size={16} />,
  database: <FiHash size={16} />,
  car: <FiHash size={16} />,
  compare: <FiMessageSquare size={16} />,
  zap: <FiZap size={16} />,
};

function generateId(): string {
  if (crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

interface ChannelChatContainerProps {
  onCommissionScan?: (pdf: FileAttachment, table: TableData, conversationId: string) => void;
  onResetScan?: () => void;
}

const ChannelChatContainer: React.FC<ChannelChatContainerProps> = ({ onCommissionScan, onResetScan }) => {
  const { channelGuid, conversationId: routeConversationId } = useParams<{ channelGuid: string; conversationId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getChannel, getChannelMessages, addMessage, loading: channelsLoading } = useChannels();
  const { amps, initialLoading: ampsLoading } = useAmpsLoaded();
  const { setStatusTemplate } = useStatusTemplate();
  const { setRunDetailPanel } = useRunDetailPanel();
  // Clear the auto-opened run-details panel when leaving this channel's chat, so
  // it doesn't linger in the sidebar on an unrelated page.
  useEffect(() => () => setRunDetailPanel(null), [setRunDetailPanel]);

  const [conversationId, setConversationId] = useState<string | undefined>(routeConversationId);
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  const [thoughtText, setThoughtText] = useState('');
  const [currentTool, setCurrentTool] = useState<string | undefined>();
  const [currentToolLabel, setCurrentToolLabel] = useState<string | undefined>();
  const [planSteps, setPlanSteps] = useState<PlanStep[]>([]);
  const [streamingContent, setStreamingContent] = useState('');
  const [lastStreamChunkAt, setLastStreamChunkAt] = useState(0);
  const [showStreamGapSkeleton, setShowStreamGapSkeleton] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState('');
  const [recentHistory, setRecentHistory] = useState<ChatThread[]>([]);

  // An archived chat leaves "Recent Conversations" right away.
  useEffect(() => {
    const onArchived = (e: Event) => {
      const id = (e as CustomEvent<{ conversationId: string }>).detail?.conversationId;
      if (id) setRecentHistory((prev) => prev.filter((t) => t.id !== id));
    };
    window.addEventListener(historyService.CHAT_ARCHIVED_EVENT, onArchived);
    return () => window.removeEventListener(historyService.CHAT_ARCHIVED_EVENT, onArchived);
  }, []);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [scanPdfAttachment, setScanPdfAttachment] = useState<FileAttachment | null>(null);
  const [scanTable, setScanTable] = useState<TableData | null>(null);
  const [previewFile, setPreviewFile] = useState<{ name: string; url: string } | null>(null);

  const displayedContent = useTypewriter(streamingContent, streamingContent.length > 0);
  const scrollRef = useAutoScroll(messages, displayedContent || thoughtText);

  useEffect(() => {
    if (!streamingContent) {
      setShowStreamGapSkeleton(false);
      return;
    }

    const tick = () => {
      const staleMs = Date.now() - lastStreamChunkAt;
      setShowStreamGapSkeleton(!isThinking && staleMs >= 1200);
    };

    tick();
    const timer = window.setInterval(tick, 250);
    return () => window.clearInterval(timer);
  }, [streamingContent, lastStreamChunkAt, isThinking]);
  const activeConvRef = useRef<string | undefined>(conversationId);
  const prevRouteIdRef = useRef<string | undefined>(routeConversationId);
  const prevChannelRef = useRef<string | undefined>(channelGuid);
  const preloadedHistoryRef = useRef<Map<string, ChatMessageType[]>>(new Map());
  const ampSwitchedRef = useRef(false);
  const prevConversationIdRef = useRef<string | undefined>(undefined);

  const channel = channelGuid ? getChannel(channelGuid) : null;
  const isWelcomeChannel = channel?.name?.toLowerCase() === 'welcome';
  const showWelcomeChannel = localStorage.getItem('showWelcomeChannel') !== 'false';

  const channelAmps = useMemo(() => {
    if (!channel) return [];
    if (channel.ampIds.length === 0) return amps; // empty = all skills available
    return channel.ampIds
      .map(ampId => amps.find(amp => amp.id === ampId))
      .filter((amp): amp is Amp => amp !== undefined);
  }, [channel, amps]);
  const topAttachments = useMemo(() => {
    const seen = new Set<string>();
    const rows: FileAttachment[] = [];
    for (const msg of messages) {
      const attachments = [...(msg.metadata?.attachments ?? []), ...extractFilesFromMessageContent(msg.content)];
      for (const att of attachments) {
        const key = (att.fileUrl || att.preview || att.name || '').trim().toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        rows.push(att);
      }
    }
    return rows;
  }, [messages]);

  const handleDismissWelcome = useCallback(async () => {
    try {
      await dismissWelcomeChannel();
    } catch {
      // proceed even if API fails
    }
    localStorage.setItem('showWelcomeChannel', 'false');
    navigate('/chat', { replace: true });
  }, [navigate]);

  const [selectedAmpIdState, setSelectedAmpIdState] = useState<string | null>(null);
  const [userOverride, setUserOverride] = useState(false);

  const selectedAmpId = useMemo(() => {
    if (userOverride && selectedAmpIdState && channelAmps.find(a => a.id === selectedAmpIdState)) {
      return selectedAmpIdState;
    }
    const ampFromUrl = searchParams.get('amp');
    if (ampFromUrl && channelAmps.find(a => a.id === ampFromUrl)) {
      return ampFromUrl;
    }
    if (selectedAmpIdState && channelAmps.find(a => a.id === selectedAmpIdState)) {
      return selectedAmpIdState;
    }
    return null;
  }, [searchParams, selectedAmpIdState, channelAmps, userOverride]);

  const setSelectedAmpId = useCallback((ampId: string | null) => {
    setSelectedAmpIdState(ampId);
    setUserOverride(true); 
  }, []);

  const selectedAmp = useMemo(() => {
    if (!selectedAmpId) return null;
    return channelAmps.find(amp => amp.id === selectedAmpId) || null;
  }, [selectedAmpId, channelAmps]);

  // Fetch rate token when the "Rate & Submission Builder", "Leads & Prospect Manager", or "Renewal Manager" AMP is selected
  useEffect(() => {
    if (!selectedAmpId) return;
    const amp = channelAmps.find(a => a.id === selectedAmpId);
    if (!amp || !['Rate & Submission Builder', 'Leads & Prospects', 'Renewals'].includes(amp.name)) return;

    const amsToken = localStorage.getItem('amsToken');
    if (!amsToken) return;
    fetch(`${(process.env.REACT_APP_Auth_Url)}/api/account/ratetoken?nctoken=${encodeURIComponent(amsToken)}`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (!data) return;
         if (data.igToken) {
          localStorage.setItem('rateToken', data.igToken);
        }
      })
      .catch(err => console.error('Failed to fetch rate token:', err));
  }, [selectedAmpId, channelAmps]);

  // Handle route changes, channel changes, and new chat
  useEffect(() => {
    const prevRouteId = prevRouteIdRef.current;
    const prevChannel = prevChannelRef.current;
    prevRouteIdRef.current = routeConversationId;
    prevChannelRef.current = channelGuid;

    if (channelGuid !== prevChannel) {
      setMessages([]);
      setSelectedAmpIdState(null);
      setUserOverride(false);
      setIsThinking(false);
      setThoughtText('');
      setCurrentTool(undefined);
      setStreamingContent('');
      setPendingPrompt('');
      setConversationId(undefined);
      setStatusTemplate(null);
      setScanPdfAttachment(null);
      setScanTable(null);
      onResetScan?.();
    }


    if (!routeConversationId && channelGuid) {
      setMessages([]);
      setIsThinking(false);
      setThoughtText('');
      setCurrentTool(undefined);
      setStreamingContent('');
      setPendingPrompt('');
      setStatusTemplate(null);
      setScanPdfAttachment(null);
      setScanTable(null);
      onResetScan?.();
      const newConversationId = generateId();
      setConversationId(newConversationId);
      const queryString = searchParams.toString();
      const newUrl = `/channel/${channelGuid}/${newConversationId}${queryString ? `?${queryString}` : ''}`;
      navigate(newUrl, { replace: true });
      return;
    }

    // Case 3: Route ID exists and changed - load messages for that conversation
    if (routeConversationId && routeConversationId !== prevRouteId && selectedAmpId) {
      setConversationId(routeConversationId);
      const preloaded = preloadedHistoryRef.current.get(routeConversationId);
      if (preloaded && preloaded.length > 0) {
        preloadedHistoryRef.current.delete(routeConversationId);
        setMessages(preloaded);
      } else {
        const key = `${selectedAmpId}:${routeConversationId}`;
        const channelMsgs = getChannelMessages(key);
        const realMessages = channelMsgs.filter(m => !m.id.endsWith('-welcome'));
        setMessages(realMessages);
      }
      setIsThinking(false);
      setThoughtText('');
      setCurrentTool(undefined);
      setStreamingContent('');
    }
  }, [routeConversationId, channelGuid, selectedAmpId, navigate, searchParams, getChannelMessages]);

  useEffect(() => {
    activeConvRef.current = conversationId;
  }, [conversationId]);

  // Reset userOverride when amp query parameter changes (e.g., from sidebar click)
  useEffect(() => {
    const ampFromUrl = searchParams.get('amp');
    if (ampFromUrl && ampFromUrl !== selectedAmpIdState) {
      setUserOverride(false);
      setSelectedAmpIdState(ampFromUrl);
      // Do not clear messages — channel chat history must stay visible across AMP switches
    }
  }, [searchParams, selectedAmpIdState]);

  useEffect(() => {
    const prevConvId = prevConversationIdRef.current;
    prevConversationIdRef.current = conversationId;

    if (!conversationId) {
      setMessages([]);
      return;
    }

    // Only reload messages when the conversation itself changes, not when just the AMP switches.
    // This keeps all messages visible (e.g. 2 renewal Q&As + 1 commission Q&A) when
    // the user picks a different Channel AMP within the same conversation.
    if (conversationId === prevConvId) return;

    const key = `${selectedAmpId}:${conversationId}`;
    const channelMsgs = getChannelMessages(key);
    const realMessages = channelMsgs.filter(m => !m.id.endsWith('-welcome'));
    if (realMessages.length > 0) {
      setMessages(realMessages);
    }
  }, [selectedAmpId, conversationId, getChannelMessages]);

  // Fetch recent history when channel or conversation changes — skip if AMP just switched
  useEffect(() => {
    if (!channelGuid) { setRecentHistory([]); return; }
    if (ampSwitchedRef.current) { ampSwitchedRef.current = false; return; }
    let cancelled = false;
    setHistoryLoading(true);
    historyService.fetchRecentHistoryForAmp(channelGuid, selectedAmpId ?? undefined).then(({ threads }) => {
      if (!cancelled) { setRecentHistory(threads); setHistoryLoading(false); }
    });
    return () => { cancelled = true; };
  }, [channelGuid, conversationId]); // eslint-disable-line react-hooks/exhaustive-deps


  const handleHistoryThreadClick = useCallback(async (threadId: string) => {
    setConversationId(threadId);
    setMessages([]);
    setScanPdfAttachment(null);
    setScanTable(null);
    setHistoryLoading(true);
    const { messages: msgs, pdfUrls } = await historyService.fetchConversationDetail(threadId);
    setHistoryLoading(false);
    // Store before navigating so the route-change useEffect doesn't overwrite them
    preloadedHistoryRef.current.set(threadId, msgs);
    setMessages(msgs);

    // Restore commission scan state from history
    if (selectedAmp?.name?.toLowerCase().includes('commission')) {
      for (const msg of msgs.filter(m => m.role === 'assistant')) {
        const parsed = parseMarkdownTable(msg.content);
        if (parsed) {
          setScanTable(parsed);
          setScanPdfAttachment({ name: 'Commission File', size: 0, type: '', fileUrl: pdfUrls[0] || undefined });
          break;
        }
      }
    }

    if (channelGuid) {
      const queryString = searchParams.toString();
      navigate(
        `/channel/${channelGuid}/${threadId}${queryString ? `?${queryString}` : ''}`,
        { replace: true }
      );
    }
  }, [channelGuid, navigate, searchParams, selectedAmp]);

  const handleSend = useCallback(async (content: string, attachments?: FileAttachment[], apiPrompt?: string) => {
    if (!channelGuid) return;

    let activeConversationId = conversationId;
    if (!activeConversationId) {
      activeConversationId = generateId();
      setConversationId(activeConversationId);
      const queryString = searchParams.toString();
      navigate(
        `/channel/${channelGuid}/${activeConversationId}${queryString ? `?${queryString}` : ''}`,
        { replace: true }
      );
    }

    // Only show non-silent attachments in the chat UI; silent ones are background API context
    const displayAttachments = attachments?.filter(a => !a.silent);

    // Commission scan: capture any uploaded file from explicitly attached files (not silent refs)
    if (selectedAmp?.name?.toLowerCase().includes('commission') && displayAttachments?.length) {
      setScanPdfAttachment(displayAttachments[0]);
      setScanTable(null);
    }
    const userMsg: ChatMessageType = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date().toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      }),
      ...(displayAttachments && displayAttachments.length > 0 ? { metadata: { attachments: displayAttachments } } : {}),
    };

    setMessages(prev => [...prev, userMsg]);

    const msgKey = `${selectedAmpId}:${activeConversationId}`;
    addMessage(msgKey, userMsg);
    setIsThinking(true);
    setThoughtText('');
    setCurrentTool(undefined);
    setCurrentToolLabel(undefined);
    setPlanSteps([]);
    setStreamingContent('');
    setLastStreamChunkAt(0);
    setShowStreamGapSkeleton(false);

    const onDone = (finalMsg: ChatMessageType) => {
      setStreamingContent('');
      setIsThinking(false);
      setThoughtText('');
      setCurrentTool(undefined);
      setCurrentToolLabel(undefined);
      setPlanSteps([]);
      setMessages(prev => [...prev, finalMsg]);
      if (activeConvRef.current) {
        addMessage(`${selectedAmpId}:${activeConvRef.current}`, finalMsg);
      }
      // Commission Statements: parse table from response
      if (selectedAmp?.name === 'Commission Statements') {
        const parsed = parseMarkdownTable(finalMsg.content);
        if (parsed) setScanTable(parsed);
      }
    };

    try {
      const filePaths = attachments
        ?.filter(att => !!att.fileUrl)
        .map(att => ({ fileName: att.name, fileURL: att.fileUrl! })) || [];

      if (isWelcomeChannel) {
        await sendMarketingMessage(content, activeConversationId, {
          onChunk: (textSoFar) => {
            setIsThinking(false);
            setLastStreamChunkAt(Date.now());
            setShowStreamGapSkeleton(false);
            setStreamingContent(textSoFar);
          },
          onDone,
        }, filePaths);
      } else {
        // Routing/skill detection now happens server-side on the channel-chat
        // endpoint — no /api/identify-skill pre-flight call needed anymore.
        const chatUrl = selectedAmp?.chatUrl || '/chat';
        const lastImageUrl = chatService.getLastImageUrl(messages);

        await chatService.sendMessage(apiPrompt ?? content, chatUrl, {
          onThought: (thoughtSoFar, tool, toolLabel) => {
            setIsThinking(true);
            setThoughtText(thoughtSoFar);
            if (tool) setCurrentTool(tool);
            if (toolLabel) setCurrentToolLabel(toolLabel);
          },
          onChunk: (textSoFar) => {
            setIsThinking(false);
            setLastStreamChunkAt(Date.now());
            setShowStreamGapSkeleton(false);
            setStreamingContent(textSoFar);
          },
          onDone,
          onPlanSteps: (steps) => {
            setPlanSteps(steps);
          },
        }, activeConversationId, filePaths, selectedAmpId ?? undefined, channelGuid, lastImageUrl);
      }
    } catch {
      setIsThinking(false);
      setStreamingContent('');
      setLastStreamChunkAt(0);
      setShowStreamGapSkeleton(false);
      setThoughtText('');
      setCurrentToolLabel(undefined);
      setPlanSteps([]);
    }
  }, [channelGuid, selectedAmpId, conversationId, addMessage, selectedAmp, isWelcomeChannel, currentToolLabel, planSteps, messages, navigate, searchParams, onResetScan, setStatusTemplate]);

  const handlePromptClick = useCallback((prompt: string) => {
    setPendingPrompt(prompt);
    void handleSend(prompt);
  }, [handleSend]);

  const handleCreateChannel = useCallback(() => {
    window.dispatchEvent(new CustomEvent('openAddChannel'));
  }, []);

  const handleAmpSelect = useCallback((ampId: string) => {
    ampSwitchedRef.current = true;
    setSelectedAmpId(ampId);
    setStatusTemplate(null);
    setScanPdfAttachment(null);
    setScanTable(null);
    onResetScan?.();
    if (channelGuid) {
      const existingId = conversationId ?? generateId();
      if (!conversationId) setConversationId(existingId);
      navigate(`/channel/${channelGuid}/${existingId}?amp=${ampId}`, { replace: true });
    }
  }, [channelGuid, conversationId, navigate, setStatusTemplate]);


  const isLoading = channelsLoading || ampsLoading;

  if (isLoading) {
    return (
      <div className="channel-chat">
        <div className="chat-loader">
          <div className="chat-loader__welcome">
            <div className="chat-loader__icon-skeleton" />
            <div className="chat-loader__line chat-loader__line--title" />
            <div className="chat-loader__line chat-loader__line--desc" />
            <div className="chat-loader__line chat-loader__line--desc2" />
          </div>
          <div className="chat-loader__prompts">
            {[0, 1, 2, 3, 4, 5].map(i => (
              <div key={i} className="chat-loader__prompt-card" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!channel) {
    return (
      <div className="channel-not-found">
        <div className="channel-not-found__content glass-card">
          <FiGrid size={48} />
          <h2>Workspace Not Found</h2>
          <p>This workspace doesn't exist or has been deleted.</p>
          <button
            className="glass-btn glass-btn-primary"
            onClick={() => navigate('/chat')}
          >
            Go to Chat
          </button>
        </div>
      </div>
    );
  }


  const showWelcome     = messages.length === 0 && !isThinking;
  const hasActiveStream = thoughtText || streamingContent;

  // Build exactly 4 prompt cards, round-robining through AMPs
  const displayPromptCards = (() => {
    const amps = channelAmps.slice(0, 4);
    if (amps.length === 0) return [];
    const promptArrays = amps.map(amp => ({ amp, prompts: buildAmpPrompts(amp) }));
    const cards: { amp: typeof amps[0]; prompt: ReturnType<typeof buildAmpPrompts>[0]; key: string }[] = [];
    let round = 0;
    while (cards.length < 4) {
      let added = 0;
      for (const { amp, prompts } of promptArrays) {
        if (cards.length >= 4) break;
        if (round < prompts.length) {
          cards.push({ amp, prompt: prompts[round], key: `${amp.id}-${round}` });
          added++;
        }
      }
      if (added === 0) break;
      round++;
    }
    return cards;
  })();

  return (
    <div className="channel-chat">
      {previewFile && (
        <FilePreviewModal
          name={previewFile.name}
          url={previewFile.url}
          onClose={() => setPreviewFile(null)}
        />
      )}
      <div className={`channel-chat__container${topAttachments.length > 0 ? ' channel-chat__container--has-top-files' : ''}`} ref={scrollRef}>
        <div className={`channel-chat__messages${messages.length > 0 ? ' channel-chat__messages--has-messages' : ''}`}>

          {/* ── Welcome channel: dedicated hero page ── */}
          {isWelcomeChannel ? (
            showWelcome && showWelcomeChannel && (
              <WelcomeChannelPage
                onPromptClick={handlePromptClick}
                onCreateChannel={handleCreateChannel}
                onDismiss={handleDismissWelcome}
              />
            )
          ) : showWelcome ? (
            /* ── Regular channels: header hidden once chat starts ── */
            <motion.div
              className="channel-welcome"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <div className="channel-welcome__icon-wrap">
                <div
                  className="channel-welcome__icon"
                  style={{
                    background: selectedAmp ? `linear-gradient(135deg, ${selectedAmp.color}28, ${selectedAmp.color}14)` : 'linear-gradient(135deg, rgba(139,92,246,0.18), rgba(99,102,241,0.10))',
                    color: selectedAmp?.color ?? 'var(--accent-purple)',
                    border: selectedAmp ? `1px solid ${selectedAmp.color}22` : '1px solid rgba(139,92,246,0.20)',
                  }}
                >
                  {selectedAmp ? (AMP_ICON_MAP[selectedAmp.icon] || <FiZap size={24} />) : (ICON_MAP[channel.icon] || <FiHash size={24} />)}
                </div>
                <div
                  className="channel-welcome__icon-glow"
                  style={{ background: selectedAmp?.color ?? '#8b5cf6' }}
                />
              </div>
              <h2 className="channel-welcome__title">{selectedAmp ? selectedAmp.name : `#${channel.name}`}</h2>
              <p className="channel-welcome__desc">{selectedAmp ? selectedAmp.description : channel.description}</p>
              <div className="channel-welcome__hints">
                {selectedAmp ? (
                  <>
                    <span className="channel-welcome__hint">Chat with {selectedAmp.name}</span>
                    <span className="channel-welcome__hint">Select a different AMP from the dropdown below</span>
                    <span className="channel-welcome__hint">All conversations are saved</span>
                  </>
                ) : (
                  <>
                    <span className="channel-welcome__hint">Ask questions specific to this channel</span>
                    <span className="channel-welcome__hint">Get AI-powered insights and assistance</span>
                    <span className="channel-welcome__hint">All conversations are saved</span>
                  </>
                )}
              </div>
            </motion.div>
          ) : null}

          {/* ── Regular channel prompt cards ── */}
          {!isWelcomeChannel && showWelcome && (selectedAmp ? buildAmpPrompts(selectedAmp) : displayPromptCards).length > 0 && (
            <div className="channel-prompts">
              {selectedAmp
                ? buildAmpPrompts(selectedAmp).map((item, i) => (
                    <motion.button
                      key={item.id}
                      className="channel-prompts__card glass-surface"
                      onClick={() => handlePromptClick(item.prompt)}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 + i * 0.06, duration: 0.35 }}
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span className="channel-prompts__icon" style={{ color: item.color }}>
                        {item.icon}
                      </span>
                      <span className="channel-prompts__prompt">{item.prompt}</span>
                    </motion.button>
                  ))
                : displayPromptCards.map(({ amp, prompt, key }, i) => (
                    <motion.button
                      key={key}
                      className="channel-prompts__card glass-surface"
                      onClick={() => { setSelectedAmpId(amp.id); setTimeout(() => handlePromptClick(prompt.prompt), 0); }}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 + i * 0.06, duration: 0.35 }}
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span className="channel-prompts__icon" style={{ color: amp.color }}>
                        {AMP_ICON_MAP[amp.icon] || <FiZap size={18} />}
                      </span>
                      <span className="channel-prompts__prompt">{prompt.prompt}</span>
                    </motion.button>
                  ))
              }
            </div>
          )}

          {!isWelcomeChannel && messages.length === 0 && (historyLoading || recentHistory.length > 0) && (
            <motion.div
              className="channel-history"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.15 }}
            >
              <div className="channel-history__header">
                <FiMessageSquare size={13} />
                <span>Recent Conversations</span>
              </div>
              {historyLoading ? (
                <div className="channel-history__loading">
                  <div className="chat-history-loader chat-history-loader--compact" aria-live="polite" aria-label="Loading recent conversations">
                    <div className="chat-history-loader__head">
                      <div className="chat-history-loader__logo-wrap" aria-hidden="true">
                        <span className="chat-history-loader__ring chat-history-loader__ring--outer" />
                        <span className="chat-history-loader__ring chat-history-loader__ring--inner" />
                        <img src={aiLogo} alt="" className="chat-history-loader__logo" />
                      </div>
                      <span className="chat-history-loader__title">Loading history</span>
                    </div>
                    <div className="chat-history-loader__line" />
                    <div className="chat-history-loader__line chat-history-loader__line--mid" />
                    <div className="chat-history-loader__line chat-history-loader__line--short" />
                  </div>
                </div>
              ) : (
                <div className="channel-history__list">
                  {recentHistory.map((thread, i) => (
                    <motion.div
                      key={thread.id}
                      role="button"
                      tabIndex={0}
                      className="channel-history__item has-archive-chat"
                      onClick={() => handleHistoryThreadClick(thread.id)}
                      onKeyDown={(e) => {
                        if (e.target !== e.currentTarget) return;
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleHistoryThreadClick(thread.id); }
                      }}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.04 }}
                      whileHover={{ x: 4 }}
                    >
                      <FiMessageSquare size={13} className="channel-history__item-icon" />
                      <span className="channel-history__item-title">{thread.title}</span>
                      <span className="channel-history__item-time">
                        {new Date(thread.updatedAt).toLocaleDateString()}
                      </span>
                      <ArchiveChatButton conversationId={thread.id} />
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {topAttachments.length > 0 && (
            <>
            <div className="chat-top-files-bar">
            <div className="chat-top-files-row">
              <span className="chat-top-files-label">
                Files
              </span>
              {topAttachments.map((file, idx) => {
                const fileLink = file.fileUrl || file.preview;
                const displayName = decodeURIComponent((file.name || '').replace(/\+/g, ' '))
                  .replace(/\s*,\s*/g, ', ')
                  .replace(/\s+/g, ' ')
                  .trim();
                const canOpen = !!fileLink;
                return (
                  <button
                    key={`channel-chat-top-file-${displayName}-${idx}`}
                    type="button"
                    disabled={!canOpen}
                    title={displayName}
                    onClick={() => {
                      if (!fileLink) return;
                      setPreviewFile({ name: displayName, url: fileLink });
                    }}
                    className="chat-top-file-chip"
                    style={{ cursor: canOpen ? 'pointer' : 'default', opacity: canOpen ? 1 : 0.65 }}
                  >
                    <FiPaperclip size={11} />
                    <span className="chat-top-file-chip__name">{displayName}</span>
                  </button>
                );
              })}
            </div>
            </div>
            </>
          )}

          {messages.map((msg, idx) => (
            <ChatMessage
              key={msg.id}
              message={msg}
              chatId={conversationId}
              isLatest={idx === messages.length - 1}
              onQuickReply={
                idx === messages.length - 1 && msg.role === 'assistant' && !hasActiveStream && !isThinking
                  ? handleSend
                  : undefined
              }
              // A carrier choice card (several policies) sends the person's pick as their next message.
              onChoose={!hasActiveStream && !isThinking ? (text: string) => handleSend(text) : undefined}
            />
          ))}

          {isThinking && !thoughtText && !streamingContent && (
            <motion.div
              className="chat-msg chat-msg--assistant chat-msg--thinking"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="chat-msg__header">
                <div className="chat-msg__avatar">
                  <div className="chat-msg__avatar-ai">
                    <img src={aiLogo} alt="AI" className="chat-msg__avatar-ai-img" />
                  </div>
                </div>
                <span className="chat-msg__sender">Insurance Agency AI</span>
              </div>
              <div className="chat-msg__body">
                <div className="thinking-indicator">
                  <div className="thinking-indicator__shimmer" />
                  <span className="thinking-indicator__text">
                    <RiSparklingFill size={12} className="thinking-indicator__icon" />
                    Thinking...
                  </span>
                </div>
              </div>
            </motion.div>
          )}

          {hasActiveStream && (
            <ChatMessage
              message={{
                id: 'streaming',
                role: 'assistant',
                content: displayedContent,
                isStreaming: true,
                timestamp: new Date().toLocaleTimeString([], {
                  hour: 'numeric',
                  minute: '2-digit',
                }),
                metadata: {
                  ...(thoughtText ? { thoughtText } : {}),
                  ...(currentTool ? { currentTool } : {}),
                  ...(currentToolLabel ? { currentToolLabel } : {}),
                  ...(planSteps.length > 0 ? { planSteps } : {}),
                },
              }}
              showStreamingGapIndicator={showStreamGapSkeleton}
            />
          )}
        </div>
      </div>

      {selectedAmp?.name?.toLowerCase().includes('commission') && !!scanPdfAttachment && !isThinking && !streamingContent && (
        <div className="amp-advanced-bar">
          <motion.button
            className="amp-advanced-btn"
            onClick={() => onCommissionScan?.(scanPdfAttachment!, scanTable!, conversationId ?? '')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
          >
            <FiSettings size={13} />
            Advanced Options
          </motion.button>
        </div>
      )}

      <div className="channel-chat__input-area">
        <ChatInput
          onSend={handleSend}
          disabled={isThinking || streamingContent.length > 0}
          placeholder={`Message ${selectedAmp ? selectedAmp.name : `#${channel.name}`}...`}
          externalValue={pendingPrompt}
          selectedAmpId={selectedAmpId}
          onAmpSelect={handleAmpSelect}
          conversationId={conversationId}
        />
      </div>

    </div>
  );
};

export default ChannelChatContainer;

